/**
 * What makes a toast appear: the notification feed, and a diff run only when the feed says
 * something happened.
 *
 *     load -> watchNotifications( /count -> /notifications )
 *               |                              |
 *               |                              +-- anything newer than this tab's mark?
 *               |                                    yes -> reconcile() -> the queue
 *               +-- /count rises later -> onNotifications -> reconcile() -> the queue
 *
 * **The reconcile is the expensive half and it used to run on every single page load.** Four
 * requests -- /api/users/me, /api/tiers, /api/badges, /api/users/me/badges -- on every navigation,
 * against somebody else's gateway, for a fan who overwhelmingly has nothing new. That was not a
 * choice: until 2026-08-11 a badge award posted NOTHING to the notification feed, so the feed could
 * not be the trigger and re-reading `getBadgesDetail()` was the only thing that could ever find a
 * badge.
 *
 * Those four used to be TWO ROUND TRIPS deep, because `getBadgesDetail` read /api/users/me for a
 * user id and only then the per-fan trail. @drewling/urc-loyalty-api 0.7.0 moved that trail from
 * the admin-only /api/badges/audit to the self-scoped /api/users/me/badges, so the badge half is
 * now two parallel reads and the whole diff is one round trip. Same request COUNT, half the
 * latency -- and, because that leg is now unguarded, a refused badge read reaches `observe()`
 * below instead of being silently reported as "no badges earned".
 *
 * **3Halves fixed that on 2026-08-11, verified live** -- an award now posts
 * `You have earned the "<name>" badge!`, six awards for six rows, and the library's classifier
 * matches it unchanged (urc-loyalty-lib, packages/urc-loyalty-api/docs/
 * notifications-verification-2026-08-11.md). Tier changes were always in the feed. So the feed is
 * now a complete doorbell, and the reconcile only has to answer the door.
 *
 * That makes the feed COMPLETENESS a load-bearing assumption of this file: an event that posts no
 * notification is an event no toast will ever announce. There is deliberately no client-side safety
 * net -- no reconcile heartbeat, no periodic floor -- because a heartbeat is the per-page cost this
 * change exists to remove, just spread out.
 *
 * The list read that decides all this is FREE: `watchNotifications` reads the list on its first tick
 * regardless, to seed a baseline it then emits nothing from. Wrapping `feed` is how this file reads
 * that same response, against a mark that -- unlike the library's -- survives the navigation.
 */

import { isAccountUnavailable, watchNotifications } from '@drewling/urc-loyalty-api';
import { PREFETCH_CACHE, reconcileLoyaltyToasts } from '@drewling/urc-loyalty-ui';
import { getClient } from '../client';
import { refreshSession } from '../session';
import { describeLoyaltyError } from '../errorDetail';
import { hasToastSnapshot, watchToastIdentity } from './identity';
import { pushToasts } from './queue';
import { hasArrivalsSince, rememberWatermark } from './watermark';
import { authState } from '../urcAuth';

/**
 * How often /api/users/me/notifications/count is asked, per VISIBLE tab -- a hidden one holds no
 * timer at all, so this is not a background cost.
 *
 * 60s, and the number is a judgement rather than a measurement, because nobody has measured one:
 * the library refuses to default it precisely because a wrong value multiplies request volume
 * against a gateway that is not ours. The payload is a congratulation, not an alert, so a worst
 * case of one minute is imperceptible, and the failure profile is what settles it -- back-off is
 * `intervalMs * min(2 ** failures, 8)`, so 60s tops out at a quiet eight minutes where 15s would
 * top out at two and hammer a gateway that has already proven fragile.
 */
const POLL_MS = 60_000;

/**
 * How long a toast stays before dismissing itself. The library requires an explicit choice -- the
 * type is `number | null` -- because "stays until closed" is a real business state, not a default
 * worth hiding. A pending timer pauses while the toast is hovered or focused, so six seconds is
 * six seconds of *unattended* life.
 */
const AUTO_DISMISS_MS = 6_000;

/** Overridable only by the dev hook, so a capture can freeze a toast. */
let autoDismissMs = AUTO_DISMISS_MS;

/** Whether a reconcile is in flight. */
let inFlight = false;

/** Whether something asked for a reconcile while one was running. */
let pendingRerun = false;

/** Where the layer publishes what it is doing. Set by start(). */
let report = () => {};

/**
 * One line per failure channel for the life of the page.
 *
 * On a host whose origin is off the gateway's CORS allowlist every single poll fails, so an
 * unthrottled warn turns the console into a scroll of identical network errors and buries whatever
 * the developer actually opened it for. A counter rather than a time window: readable from the
 * module, and testable by eye.
 */
const logged = { reconcile: 0, watch: 0, stopped: 0, catchup: 0 };

/**
 * Whether the one permitted watcher restart has been spent.
 *
 * `watchNotifications` stops for good on a `requiresReauth` error, which is usually right -- a
 * sign-out surfaces as exactly that, raised before the wire, so the loop dies with no network
 * traffic. It is not always right: the Auth0 SDK can fail to answer a token request for a reason
 * that is not "nobody is signed in" -- a refused audience, a transient network failure inside its
 * silent-auth attempt -- and `resources/scripts/auth/runtime.js` warns about exactly that rather
 * than pretending it is a sign-out. One flaky answer would otherwise kill the watcher permanently.
 * Hence one restart, hard-capped, never a ladder: a genuinely dead session must not become a
 * reauth loop.
 *
 * (Until 2026-09-07 the reason was narrower and is worth recording as gone: session.js resolved a
 * tokenless session when an admin-ajax call merely FAILED, and memoised it for 30s.)
 */
let restartSpent = false;

/**
 * @param {unknown} error   Whatever was thrown.
 * @param {string}  channel A key of `logged`.
 * @param {string}  message Human-readable prefix.
 * @return {void}
 */
function warnOnce( error, channel, message ) {
	if ( logged[ channel ] > 0 ) {
		return;
	}

	logged[ channel ] += 1;

	console.warn( `[loyalty:toast] ${ message }`, describeLoyaltyError( error ) );
}

/**
 * Re-read the loyalty state, diff it against this tab's snapshot, and queue whatever is new.
 *
 * Single-flighted with a trailing re-run. The library says a double reconcile is idempotent, and
 * that holds while both succeed -- but a PARTIAL failure racing a success does not: the failing run
 * leaves `earnedBadgeIds` at its own stale value and writes that back over the successful run's
 * snapshot, so the badge that already toasted toasts again on the next pass. A flag is cheaper than
 * reasoning about that every time somebody adds a caller.
 *
 * Resolves to whether every read behind the diff SUCCEEDED, because `probeFeed()` uses that to decide
 * whether to advance the feed mark. A mark advanced past a read that failed drops the arrival for
 * good: /count does not rise twice for the same row.
 *
 * **"The promise resolved" is not that answer, and this cost a wrong guard once.**
 * `reconcileLoyaltyToasts` runs its three reads under `Promise.allSettled` and resolves normally when
 * every one of them fails -- returning no items, and correctly leaving the snapshot's slices at their
 * previous values, because `null` there means "never read". Driven against a stubbed `/api/users/me`
 * returning 500, the whole diff resolved and reported nothing wrong. So completeness is measured here
 * instead, by handing the library a source that records a rejection on the way past. `ToastDiffSource`
 * is a three-method structural interface, which is what makes that free -- the dev `replay()` hook in
 * index.js passes a synthetic one for its own reasons.
 *
 * @return {Promise<boolean>} Whether all three reads succeeded and the diff was applied.
 */
export function reconcileToasts() {
	if ( inFlight ) {
		pendingRerun = true;

		// Not "it worked" -- the trailing re-run below will cover this arrival, and a caller that
		// held off advancing its mark simply looks again. The other way round loses the row.
		return Promise.resolve( false );
	}

	inFlight = true;

	const client = getClient();

	// Mandatory, not hygiene. `getBadgesDetail` is in client.js's PREFETCH_METHODS with a 30s TTL,
	// and that memo is keyed by method name -- so without this every reconcile inside a 30s window
	// diffs the same settled overview and the badge branch can never fire at all. The symbol is off
	// ILoyaltyAPI's string-keyed surface and absent on an unwrapped client, hence the optional call.
	client[ PREFETCH_CACHE ]?.invalidate( 'getBadgesDetail' );

	let complete = true;

	/**
	 * Note a failed leg, and re-throw so the library still sees the rejection it settles on.
	 *
	 * @param {Promise} read One of the three source reads.
	 * @return {Promise} The same read.
	 */
	const observe = read =>
		read.catch( error => {
			complete = false;

			throw error;
		} );

	const source = {
		getTierProgress: opts => observe( client.getTierProgress( opts ) ),
		getTiers: opts => observe( client.getTiers( opts ) ),
		getBadgesDetail: opts => observe( client.getBadgesDetail( opts ) ),
	};

	return reconcileLoyaltyToasts( source, { autoDismissMs } )
		.then( pushToasts )
		.then( () => complete )
		.catch( error => {
			warnOnce( error, 'reconcile', 'reconcile failed' );

			return false;
		} )
		.then( ok => {
			inFlight = false;

			if ( ! pendingRerun ) {
				return ok;
			}

			pendingRerun = false;

			return reconcileToasts();
		} );
}

/**
 * One list read, and a reconcile only if that read says something happened.
 *
 * This is the whole of the lazy trigger, and it is the single place the decision is made -- the
 * watcher's list reads and the bfcache catch-up both go through it.
 *
 * Two conditions run the reconcile, and they are different questions:
 *
 *   - `hasToastSnapshot()` -- is there a baseline to diff against? Without one,
 *     `reconcileLoyaltyToasts` can only re-baseline and return nothing, so skipping the reconcile
 *     here would mean the arrival that prompted it is what gets baselined, and it is swallowed.
 *     identity.js keeps the snapshot and the mark clearing together so this stays a rare case, but
 *     the rule is stated rather than inferred from that. **It is also what makes an unkeyed feed
 *     mark safe**: the snapshot is cleared on an identity change, so `! hasToastSnapshot()` forces
 *     the reconcile even if a previous fan's mark is still in storage. Do not "simplify" the `||`.
 *   - `hasArrivalsSince()` -- did anything land since this TAB last looked? The library's own
 *     high-water mark cannot answer that; it is re-seeded on every navigation.
 *
 * The mark advances only behind a reconcile that actually ran. Advancing it after a failure would
 * drop the row for good: /count does not rise twice for the same notification.
 *
 * Errors propagate. Inside the watcher that is required -- a swallowed read failure would leave the
 * back-off ladder thinking it succeeded. The one caller with nowhere to throw catches for itself.
 *
 * It AWAITS the reconcile rather than firing it off, which means a watcher tick lasts as long as the
 * diff behind it and the next poll is scheduled that much later. That is the price of only advancing
 * the mark behind a run that succeeded, and it is bounded by the client's own 10s timeout.
 *
 * @param {{signal?: AbortSignal}} [opts] Passed straight through to the client.
 * @return {Promise<Array>} The rows, for whoever asked for them.
 */
async function probeFeed( opts ) {
	const rows = await getClient().getNotifications( opts );

	// The fan is read here rather than captured, so a mark can never be written under the fan who
	// owned the page when the watcher started. `./watermark.js` keys the mark by `sub`, and an
	// unidentifiable fan gets no mark at all -- which reads as "always arrivals".
	const state = authState();
	const fan = state && state.sub ? state.sub : '';

	if ( ! hasToastSnapshot() || hasArrivalsSince( rows, fan ) ) {
		if ( await reconcileToasts() ) {
			rememberWatermark( rows, fan );
		}
	}

	return rows;
}

/**
 * The two reads `watchNotifications` needs, with the list read tapped on the way past.
 *
 * `watchNotifications` reads the list on its FIRST tick regardless -- to seed a baseline it
 * deliberately emits nothing from -- and again whenever /count rises. Both are responses this file
 * wants to look at, so tapping them here costs no request at all. That is the entire reason the
 * per-page reconcile could go away.
 *
 * @return {import('@drewling/urc-loyalty-api').NotificationFeed} The feed the watcher polls.
 */
function feed() {
	return {
		getNotificationCount: opts => getClient().getNotificationCount( opts ),
		getNotifications: probeFeed,
	};
}

/**
 * Start the watcher. Separate from start() so the reauth path can start a second one.
 *
 * @return {void}
 */
function watch() {
	watchNotifications( {
		feed: feed(),
		intervalMs: POLL_MS,
		// Deliberately empty, and the library documents `() => {}` as the way to say so. Every
		// arrival this would report has already been acted on inside `probeFeed()`, against a mark that
		// outlives the page -- reconciling here as well would run the whole four-request diff a
		// second time for the same row, since reconcileToasts() answers a re-entrant call with a
		// trailing re-run rather than a no-op.
		//
		// Nothing is lost by not reading the batch, and the reason changed under us. The feed used
		// to carry no structured discriminator at all -- `kind` was a regex guess over message
		// text. Rows created since 2026-08-26 do carry `eventType` and `subject`, and
		// @drewling/urc-loyalty-api 0.8.0's classifier reads the event name before falling back to
		// those prose rules. It still buys this file nothing: `subject` names the badge or tier but
		// cannot supply the BadgeDetail a toast draws, so the reconcile stays the content source
		// and the feed stays a doorbell. Historical rows were never backfilled, so the prose rules
		// are still what classify most of the feed.
		onNotifications: () => {},
		onError: ( error, info ) => {
			if ( info.retryInMs !== null ) {
				report( 'retrying' );
				warnOnce( error, 'watch', 'notification poll failed' );

				return;
			}

			// A stop is not always a dead credential since @drewling/urc-loyalty-api 0.18.0. A fan
			// whose erasure had completed was refused for good with a VALID token, so the restart
			// below would fetch the same token and be refused again. Nothing to re-authenticate,
			// nothing to restart -- say which it was and stay stopped. Defensive now: 3Halves report
			// that refusal removed on 2026-09-27 (#660, not measured here), and the library's own
			// isAccountUnavailable() says the same since 0.20.0.
			if ( isAccountUnavailable( error ) ) {
				report( 'stopped-unavailable' );
				warnOnce( error, 'stopped', 'notification poll stopped, account unavailable' );

				return;
			}

			report( 'stopped-reauth' );
			warnOnce( error, 'stopped', 'notification poll stopped, re-authentication needed' );

			if ( restartSpent ) {
				return;
			}

			restartSpent = true;

			// Asked once, and only ever after the endpoint answers again. The library stops the
			// loop BEFORE reporting, so starting a fresh one from here is not racing its teardown.
			refreshSession().then( session => {
				if ( ! session.token ) {
					return;
				}

				report( 'polling' );
				watch();
			} );
		},
	} );

	report( 'polling' );
}

/**
 * Arm the trigger loop. Called once, after the gates pass and the layer is mounted.
 *
 * **There is no reconcile here any more.** The watcher's first tick is immediate and reads the list,
 * and `feed()` reconciles off that read when there is anything to reconcile -- so the load-time
 * catch-up still happens, it just no longer happens when nothing arrived. One consequence worth
 * knowing: a page opened in a BACKGROUND tab now does nothing until it is looked at, because a hidden
 * tab holds no timer at all. That is the library's own promise ("a tab opened in the background seeds
 * its baseline when it is first looked at"), and a toast nobody can see was never worth four requests.
 *
 * @param {(state: string) => void} onState Publishes what the loop is doing.
 * @return {void}
 */
export function startToastTrigger( onState ) {
	report = onState;

	// Standing, not a one-shot: the baseline has to be dropped whenever the fan changes, including
	// while this page is open and including a switch performed in another tab. `onIdentityChange`
	// replays for a late subscriber, so arming here -- two round trips into the page -- still
	// catches a change that was detected before the gate chain settled. See ./identity.js.
	watchToastIdentity();
	watch();

	// The one lifecycle event the library does not cover. It listens for `visibilitychange` (parking
	// its timer entirely while hidden) and for `online`, but not for `pageshow` -- and a bfcache
	// restore replays neither DOMContentLoaded nor, on some engines, visibilitychange, so a restored
	// page can carry a watcher whose timer was cleared on hide and never re-armed.
	//
	// Deliberately not a fresh watcher: restarting one would re-baseline its high-water mark, and the
	// catch-up this needs is the same feed read the watcher would have done. Residual, and known: on
	// an engine that skips visibilitychange the POLL stays dead until the next navigation.
	window.addEventListener( 'pageshow', event => {
		if ( ! event.persisted ) {
			return;
		}

		// No identity guard here any more, and that is not an omission: the auth bundle re-resolves
		// on its own `pageshow` and the subscription above is what reports the answer, so a fan who
		// signed out and in elsewhere while this page sat in the cache is covered by the same
		// signal as every other switch. A second guard call here would be a second implementation
		// of the same rule, running on one of the several cues that can produce a change.
		//
		// One request, and the four heavy ones only if it found something -- the same bargain as a
		// page load. Nothing above this catches, so it catches here.
		probeFeed().catch( error => warnOnce( error, 'catchup', 'catch-up feed read failed' ) );
	} );
}

/**
 * Disable auto-dismiss for the rest of the page, so a toast can be inspected or captured.
 *
 * Dev hook only -- see index.js, where the whole surface is behind import.meta.env.DEV.
 *
 * @return {void}
 */
export function holdToasts() {
	autoDismissMs = null;
}
